import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Shield, Zap, FileText, Clock, TrendingUp, Users, Briefcase,
  Home, Heart, Gift, RefreshCw, ArrowUpCircle, Lightbulb, Star, Phone, Mail,
  MapPin, AlertTriangle, Headphones, Building, DollarSign, Percent, Calendar,
  CreditCard, Smartphone, Award, ThumbsUp, Handshake, BookOpen, Globe, Lock,
  FileCheck, ClipboardList, Landmark, ShoppingCart, Truck
} from 'lucide-react';

const BusinessLoanVaranasi = () => {
  return (
    <div className="bg-gray-50">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-green-800 to-green-700 text-white py-12 md:py-16 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in-up">
                Business Loan in Varanasi
              </h1>
              <p className="text-lg md:text-xl mb-6 animate-fade-in-up animation-delay-200">
                Fuel your business growth with flexible funding, quick approval, and competitive rates.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Apply Now
                </button>
                <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300">
                  Check Eligibility
                </button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Business Loan"
                className="max-w-full h-auto rounded-2xl shadow-xl transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* What is a Business Loan? */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">What is a Business Loan?</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 leading-relaxed">
              Those who wish to launch or expand their businesses need funds to achieve these goals. Individuals and entrepreneurs take out loans at a given interest rate and over a fixed term. Capital is essential to the survival of a business. A business can borrow capital in two ways – Secured loans and unsecured loans.
            </p>
          </div>
        </div>

        {/* Variety of Purposes */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Business Loans Serve a Variety of Purposes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: TrendingUp, title: "Operational Expansion", desc: "Scale up your business when growth opportunities arise." },
              { icon: ShoppingCart, title: "Purchasing Equipment", desc: "Upgrade technology, machinery, or lease equipment to improve productivity." },
              { icon: Truck, title: "Inventory Purchase", desc: "Buy raw materials or stock to meet market demand." },
              { icon: DollarSign, title: "Increase Working Capital", desc: "Manage day-to-day operations with short-term loans." },
              { icon: Star, title: "Boost Credit Score", desc: "Build positive credit history for future larger loans." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Business Loans in Varanasi */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Business Loans in Varanasi</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 leading-relaxed">
              If you need to improve your credit score and increase your working capital, it is best to take out a Business Loan in Varanasi. When a company applies for a loan, banks/lenders will review financial statements and business records to determine the company's creditworthiness, which is its ability to repay the loan. The disbursement of business loans is dependent on the business owner's credit score.
            </p>
          </div>
        </div>

        {/* Financing Options */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Financing Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-green-600" /> Cash Credit & Overdraft
              </h3>
              <p className="text-gray-600">
                Financial institutions offer their customers cash credit and overdrafts as short-term financing options. Both prevent checks from bouncing or debit cards from being declined when there aren't enough funds in the account. The interest is imposed only on the amount withdrawn.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-green-600" /> Dropline Overdraft
              </h3>
              <p className="text-gray-600">
                Dropline Overdrafts are financial instruments that allow borrowers to overdraw their current accounts up to a limit that decreases monthly from the sanctioned limit. A loan's interest rate is normally charged on only the cash you withdraw, not on the entire loan amount. You can reduce your outstanding balance at any time by making a deposit. The interest rate for each month is calculated daily and charged at the end of each month.
              </p>
            </div>
          </div>
        </div>

        {/* Why Choose CashPro */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Why Choose  CashPro Financial Services?</h2>
          <p className="text-gray-700 mb-6 text-center">
            Varanasi is home to almost all major banks and non-bank financial companies that provide business loans. Business loans typically have an interest rate of between 11% and 24%. A long-term loan has a maximum tenure of 15 years, unlike short-term loans of 1 to 5 years. In addition to the processing fee, GST will apply from 1% – 3%. Lenders commonly disburse loans between 50,000 and 1 crore INR. Apply for Business Loan at Lowest Interest Rate with  CashPro Financial Services and enjoy the fast and hassle‑free services.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {[
              "Lowest interest rates guaranteed",
              "Quick approval & disbursal",
              "Minimal documentation",
              "Free expert consultation",
              "Doorstep document pickup",
              "Compare offers from 30+ lenders"
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Eligibility Criteria */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Business Loan Eligibility</h2>
          <p className="text-gray-700 mb-4">
            Anyone who runs a legitimate business or has a good turnover can apply for a business loan. Different Business Loan Providers in Varanasi use various criteria for validating and authenticating a business. The bank has final decision-making authority over loan amounts, loan tenures, and interest rates.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {[
              "Firm Type: Proprietorship, Partnership, Public Limited or Private Ltd",
              "Business turnover",
              "Net profit",
              "Cash flow",
              "Business track record"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Documents Required */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Documents Required for Varanasi Business Loans</h2>
          <p className="text-gray-600 mb-4 text-sm">
            KYC document requirements vary by lender (bank or NBFC) but we've compiled a broad list for your convenience:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              "Identification Proof (PAN, Aadhaar, Voter ID, Passport, Driving License)",
              "Address Proof (Aadhaar, Utility bills, Rent agreement, Passport)",
              "Continuity of business proof (GST registration, Trade license)",
              "Six‑month bank statements",
              "Board Resolution (for companies)",
              "ITR and income computation statement – last two years",
              "CA Certified or Audited profit & loss accounts, audit reports for >40 Lakh cases",
              "Latest Income Tax Return of Co‑Applicants",
              "Proof of Address/Office (electricity bill, rent agreement, property tax receipt)"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* How to Apply */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Applying for a Business Loan in Varanasi</h2>
          <p className="text-gray-700 text-center max-w-3xl mx-auto">
            Register with  CashPro Financial Services and check your credit score to find out if you are eligible for a Business Loan in Varanasi. The process is quick, simple, and free. A credit score and credit report are the first steps towards good credit health. At a glance, your credit report will give you a sense of your credit history, which includes your current and previous accounts. These include credit cards and loans. Eliminate any negative records caused by incorrect information or unpaid balances. A credit improvement service can assist with this. A first‑time entrepreneur or someone seeking formal funding can use this form.
          </p>
          <div className="text-center mt-6">
            <button className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full transition duration-300 shadow-lg">
              Apply Online Now
            </button>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We Are Here to Help You</h2>
          <p className="text-gray-600 mb-6">Our business loan experts are ready to assist you.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-full">Apply Now</button>
            </div>
            <div>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600">+91 88871 41052</a>
            </div>
            <div>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600">info@cashpro.co.in</a>
            </div>
          </div>
          <Link to="/contact" className="text-green-600 hover:underline font-medium">Contact Us →</Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fade-up 0.6s ease-out forwards; }
        .animate-fade-in-up { animation: fade-up 0.6s ease-out forwards; }
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-400 { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};

export default BusinessLoanVaranasi;